import {createAction, createReducer} from '@reduxjs/toolkit'
import {takeEvery, getContext, put, call, select} from 'redux-saga/effects'
import {Dictionary, keyBy} from 'lodash'
import Patient from '../../models/Patient'
import Api, {UnauthorizedError} from 'Api'
import {actions as searchActions} from './search'
import {RootState} from 'redux/rootReducer'

export type PatientsState = {
  patients: Dictionary<Patient>
  total: number
  currentPage: number
  loading: boolean
  error: string | undefined
}
export const initialState: PatientsState = {
  patients: {},
  total: 0,
  currentPage: 1,
  loading: false,
  error: undefined,
}

export const actions = {
  set: createAction<{patients: Patient[], total?: number, page?: number}>('patients/set'),
  upsert: createAction<{patient: Patient}>('patients/upsert'),
  remove: createAction<{id: string}>('patients/remove'),
  get: createAction<void>('patients/get'),
  add: createAction<{patient: Patient}>('patients/add'),
  update: createAction<{patient: Patient}>('patients/update'),
  delete: createAction<{id: string}>('patients/delete'),
  resetData: createAction<{id: string}>('patients/resetData'),
  setLoading: createAction<{loading: boolean}>('patients/setLoading'),
  setError: createAction<{error?: string}>('patients/setError'),
}

export const reducer = createReducer(initialState, (builder) => {
  builder
    .addCase(actions.set, (state, action: ReturnType<typeof actions.set>) => {
      const {patients, total, page} = action.payload;
      state.patients = keyBy(patients, 'id');
      if (total !== undefined) state.total = total;
      if (page !== undefined) state.currentPage = page;
    })
    .addCase(actions.upsert, (state, action: ReturnType<typeof actions.upsert>) => {
      const {patient} = action.payload;
      state.patients[patient.id] = patient;
    })
    .addCase(actions.remove, (state, action: ReturnType<typeof actions.remove>) => {
      const {id} = action.payload;
      delete state.patients[id];
    })
    .addCase(actions.setLoading, (state, action: ReturnType<typeof actions.setLoading>) => {
      const {loading} = action.payload;
      state.loading = loading;
    })
    .addCase(actions.setError, (state, action: ReturnType<typeof actions.setError>) => {
      const {error} = action.payload;
      state.error = error;
    });
});

const getSaga = function*() {
  const api: Api = yield getContext('api');
  try {
    const {patients, total, page} = yield call(api.getPatientsPage, 1, 100);
    yield put(actions.set({patients, total, page}));
  } catch (e) {
    const error = e as any;
    if (error === UnauthorizedError) throw error;
    yield put(actions.setError({error: error?.message ?? 'Error loading patients'}));
  }
};

const addSaga = function*(action: ReturnType<typeof actions.add>) {
  const {patient} = action.payload
  const api: Api = yield getContext('api')
  yield put(actions.setLoading({loading: true}))
  yield put(actions.setError({error: undefined}))
  try {
    const saved: Patient = yield call(api.addPatient, patient)
    yield put(actions.upsert({patient: saved}))
    const query: string | undefined = yield select(({search}: RootState) => search.query)
    if (query?.trim()) yield put(searchActions.refreshSearch())
  } catch (e) {
    const error = e as any
    if (error === UnauthorizedError) {
      throw error
    }
    yield put(actions.setError({error: error.message}))
  }
  yield put(actions.setLoading({loading: false}))
}

const deleteSaga = function*(action: ReturnType<typeof actions.delete>) {
  const {id} = action.payload
  const api: Api = yield getContext('api')
  yield call(api.deletePatient, id)
  yield put(actions.remove({id}))
  yield put(searchActions.refreshSearch())
}

const updateSaga = function*(action: ReturnType<typeof actions.update>) {
  const {patient} = action.payload
  const api: Api = yield getContext('api')
  yield put(actions.setLoading({loading: true}))
  yield put(actions.setError({error: undefined}))
  try {
    const saved: Patient = yield call(api.updatePatient, patient)
    yield put(actions.upsert({patient: saved}))
    yield put(searchActions.refreshSearch())
  } catch (e) {
    const error = e as any
    if (error === UnauthorizedError) {
      throw error
    }
    yield put(actions.setError({error: error.message}))
  }
  yield put(actions.setLoading({loading: false}))
}

const resetDataSaga = function*(action: ReturnType<typeof actions.resetData>) {
  const {id} = action.payload
  const api: Api = yield getContext('api')
  yield put(actions.setLoading({loading: true}))
  yield put(actions.setError({error: undefined}))
  try {
    yield call(api.resetPatientData, id)
    yield put(searchActions.refreshSearch())
  } catch (e) {
    const error = e as any
    if (error === UnauthorizedError) {
      throw error
    }
    yield put(actions.setError({error: error.message}))
  }
  yield put(actions.setLoading({loading: false}))
}

export const saga = function*() {
  yield takeEvery(actions.delete, deleteSaga)
  yield takeEvery(actions.update, updateSaga)
  yield takeEvery(actions.get, getSaga)
  yield takeEvery(actions.add, addSaga)
  yield takeEvery(actions.resetData, resetDataSaga)
}
